import { useCallback, useEffect, useRef, useState } from "react";
import CrudService from "services/cruds-service";
import { mergeReportRows, strictNumber } from "./historyLogs";

const PAGE_SIZE = 100;

export default function useGatewayHistory(deviceId) {
  const identity = String(deviceId);
  const current = useRef(identity);
  current.current = identity;
  const generation = useRef(0);
  const active = useRef(true);
  const lock = useRef(false);
  const page = useRef(0);
  const [state, setState] = useState({
    identity: "", rows: [], loading: true, error: "", total: null,
    serverRows: 0, hasMore: false, initialized: false,
  });

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; generation.current++; };
  }, []);

  const load = useCallback(async (restart = false) => {
    if (!Number.isInteger(strictNumber(deviceId)) || strictNumber(deviceId) <= 0) {
      setState({ identity, rows: [], loading: false, error: "Invalid gateway ID.",
        total: null, serverRows: 0, hasMore: false, initialized: false });
      return;
    }
    if (lock.current) return;
    lock.current = true;
    const token = generation.current;
    const requestIdentity = identity;
    const nextPage = restart ? 1 : page.current + 1;
    setState(previous => ({
      ...previous, identity: requestIdentity, loading: true, error: "",
    }));
    try {
      const response = await CrudService.getDeviceMonitorLogs(deviceId, nextPage, PAGE_SIZE);
      if (!response?.success || !Array.isArray(response.data)) {
        throw new Error(response?.message || "Could not load gateway reports.");
      }
      const total = strictNumber(response.pagination?.total);
      if (!Number.isInteger(total) || total < 0) {
        throw new Error("Monitor logs returned invalid pagination metadata.");
      }
      // Validate raw reports before changing state; errors must be visible.
      const incoming = mergeReportRows([], response.data);
      if (!active.current || current.current !== requestIdentity || token !== generation.current) return;
      page.current = nextPage;
      setState(previous => {
        const rows = mergeReportRows(restart ? [] : previous.rows, incoming);
        const serverRows = (restart ? 0 : previous.serverRows) + response.data.length;
        return { identity: requestIdentity, rows, loading: false, error: "", total,
          serverRows, initialized: true,
          hasMore: response.data.length > 0 && serverRows < total };
      });
    } catch (error) {
      console.error("Gateway history/log lookup failed", error);
      if (active.current && current.current === requestIdentity && token === generation.current) {
        setState(previous => ({ ...previous, loading: false,
          error: error?.message || error?.error || "Could not load gateway reports." }));
      }
    } finally {
      if (token === generation.current) lock.current = false;
    }
  }, [deviceId, identity]);

  useEffect(() => {
    generation.current++;
    lock.current = false;
    page.current = 0;
    setState({ identity, rows: [], loading: true, error: "", total: null,
      serverRows: 0, hasMore: false, initialized: false });
    load(true);
  }, [identity, load]);

  const appendLive = useCallback(event => {
    if (event?.type === "duplicate" || String(event?.device_id) !== identity) return;
    try {
      const row = { data: event.data }; // Preserve event timestamp, no fake ID/date.
      const incoming = mergeReportRows([], [row]);
      setState(previous => previous.identity !== identity ? previous : ({
        ...previous, rows: mergeReportRows(previous.rows, incoming),
      }));
    } catch (error) {
      console.error("Gateway live history report rejected", error);
      setState(previous => previous.identity !== identity ? previous : ({
        ...previous, error: error?.message || "Live report could not be added to history.",
      }));
    }
  }, [identity]);

  const visible = state.identity === identity ? state : {
    identity, rows: [], loading: true, error: "", total: null,
    serverRows: 0, hasMore: false, initialized: false,
  };
  return {
    ...visible, appendLive,
    loadOlder: () => load(false),
    refresh: () => load(true),
    // Retry page 1 if initial load failed; otherwise retry the unadvanced page.
    retry: () => load(page.current === 0),
  };
}
